import { Fragment, useState, useEffect, useRef, useCallback, useMemo, type CSSProperties, type ReactNode } from "react";
import type { ApprovalRequest, Citation, EditableProposal, Message, WsServerMessage } from "@yagni/shared";
import { useWebSocket } from "../hooks/useWebSocket.js";
import { markdownHtml } from "../lib/markdown.js";
import { focusRequestedRecently, onComposerFocusRequest } from "../lib/answerComposer.js";
import { Send, Loader2, CheckCircle2, AlertCircle, Sparkles } from "lucide-react";
import { isToolCallError, type ToolCallDisplay } from "../lib/toolCallDisplay.js";
import { ToolCallLabel } from "./ToolCallLabel.js";
import { ToolCallGroup } from "./ToolCallGroup.js";
import { TypingIndicator } from "./TypingIndicator.js";
import { WorkItemMention } from "./WorkItemMention.js";
import { extractTicketIdentifiers, useWorkItemResolver } from "../hooks/useWorkItemResolver.js";
import { Textarea } from "../ui/textarea.js";
import { spliceCitationsIntoText } from "./CitationChip.js";
import { useCitationResolvers } from "../hooks/useCitationResolvers.js";
import { ComposerSuggestions } from "./ComposerSuggestions.js";
import { PrototypeArtifact, type PrototypeArtifactData } from "./PrototypeArtifact.js";
import { DecisionCard } from "../decisions/DecisionCard.js";
// The approval ask renders the canonical DecisionCard (R2), whose verb/decline/
// effect affordances are styled by the shared feed stylesheet (feed-act-*,
// feed-effect-line). Class-scoped (feed-*), so importing it here is safe.
import "../decisions/decisions.css";

// Stable empty array reference so messages without citations don't
// thrash the resolver hook's memo dependencies on every re-render.
const EMPTY_CITATIONS: Citation[] = [];

interface InlineTypingParticipant {
  senderId: string;
  senderName: string;
  senderType: "human" | "agent";
}

/* ═══════════════════════════════════════════════════════════
   Agent color mapping
   ═══════════════════════════════════════════════════════════ */

const AGENT_COLORS: Record<string, string> = {
  ops: "var(--agent-ops)",
  pm: "var(--agent-pm)",
  dev: "var(--agent-dev)",
  analytics: "var(--agent-analytics)",
  comms: "var(--agent-comms)",
  gtm: "var(--agent-gtm)",
  qa: "var(--agent-qa)",
  support: "var(--agent-support)",
  finance: "var(--agent-finance)",
  design: "var(--agent-design)",
};

function getColor(name: string): string {
  const key = name.toLowerCase().replace(/^@/, "").replace(/^agent-/, "");
  return AGENT_COLORS[key] ?? "var(--text-tertiary)";
}

/* ═══════════════════════════════════════════════════════════
   InlineChat Component
   ═══════════════════════════════════════════════════════════ */

/**
 * InlineChat is bound to one conversation (`conversationId`). Messages are
 * persisted in the isolated `conversations` table and never appear in any
 * channel. Hosts: ConversationPanel (the drafting-style artifact surfaces)
 * and the @yagni rail (YagniRail). See
 * packages/backend/src/routes/conversations.ts.
 */
type InlineChatProps = {
  conversationId: string;
  agentName: string;
  initialMessages: Message[];
  placeholder?: string;
  /**
   * Fires the instant a human message is dispatched from this chat
   * surface (after the client-side `send()` call, before any server
   * echo). Lets an enclosing surface react to "the user just typed
   * something" without subscribing to the WebSocket directly — the
   * FeedbackSidePanel uses it to optimistically clear the "resolved"
   * banner because the backend will auto-reopen the ticket on receipt.
   *
   * The `text` argument carries the trimmed message body the user sent.
   * Optional — most callers don't need it. The redesigned + New flow
   * uses it on the first send to fetch the similarity bag and PATCH
   * the conversation row with the resulting `scope_context`.
   */
  onHumanMessageSent?: (text: string) => void;
  /**
   * When set, the chat scrolls the matching message (by id) into view and
   * flashes it briefly. Generic deep-link primitive — used by Activity
   * feed rows, notifications, etc. A no-op if the id doesn't match any
   * currently-rendered message.
   */
  focusMessageId?: string | null;
  /**
   * Imperative escape hatch for parents that need to dispatch a message
   * programmatically (e.g. a seeded send into the @yagni rail). When set, the hook
   * populates `.current` with a `{ sendMessage(text) }` object. The
   * dispatch reuses the same client-side path as the Enter key so WS
   * subscriptions, `onHumanMessageSent`, and input clearing all behave
   * identically.
   *
   * The ref is cleared when the component unmounts. Callers must
   * null-check before invoking.
   */
  imperativeRef?: React.MutableRefObject<{
    sendMessage: (text: string) => void;
  } | null>;
  /**
   * When true, this composer takes keyboard focus when a decision card asks the
   * user to answer @yagni (see `lib/answerComposer`). Set on the @yagni rail /
   * in-column feed composer so the "Answer @yagni" affordance on a decision lands
   * the cursor in the right place. Default false leaves every other host alone.
   */
  focusOnAnswerRequest?: boolean;
  /**
   * Optional quick-fill suggestions rendered as chips above the composer.
   * Clicking a chip POPULATES the composer (it does not auto-send) so the
   * user can edit before sending. Empty / undefined → no chips rendered.
   */
  composerSuggestions?: string[];
  /**
   * Composer chrome variant.
   * - "classic" (default): the shared `.conversation-composer` used by
   *   ConversationPanel.
   * - "tri": the Triptych `.tri-chat-foot` — a suggestion-chip row over a
   *   `.cw-composer` with an always-amber `.cw-send`. Used by the in-stage
   *   @yagni rails (Desk / Settings / Work). The amber accent is read from the
   *   enclosing `.tri-chat`'s `--dc` custom property, so the rail must set it.
   * In the "tri" variant `composerSuggestions` render as `.cw-sugg` chips that
   * send on click (a scope question like "What's stalled?"); a chip ending in
   * "…" fills the composer instead ("Start a ticket for …").
   */
  composerVariant?: "classic" | "tri" | "dock";
  /**
   * The "dock" variant has no composer of its own: the Ask dock owns the input
   * and sends through `imperativeRef`. Its suggestion chips print as the
   * dock's `.prompts` row; a chip that trails off ("Start a ticket for …")
   * hands its text to this callback so the dock can fill its input.
   */
  onSuggestionFill?: (text: string) => void;
  /**
   * A static, @yagni-authored opening line shown as an agent message bubble
   * when the thread is genuinely empty (no persisted history, nothing
   * streaming). It is purely presentational — never persisted, never sent, and
   * it triggers no LLM turn. The instant any real message exists (hydrated from
   * history or streamed in live) it gives way to the thread.
   *
   * Rendering is held until the mount-time hydrate resolves so a returning user
   * with prior history never sees the opener flash before their chat loads.
   * While the opener is showing, the composer takes a brief, subtle invite cue
   * (`data-invite`) so the eye lands on where to type. The @yagni rail always
   * passes one; without it the thread shows ConversationEmptyState.
   */
  openingMessage?: string;
  /**
   * Optional scope context the enclosing surface attaches to every outgoing
   * conversation message (YAG-326, Task 7). The Library review rail passes the
   * bound work item's id + phase + role + desk Number label so @yagni answers
   * in-context. Sent additively on `sendConversationMessage` — omitted when
   * absent, so every other host is unchanged.
   */
  scopeContext?: Record<string, unknown>;
  /**
   * Optional content under one message, for a host that offers something to
   * do with it (the @yagni rail's "Use as answer" on a reply to a plan
   * question). Called for every visible message; return null for none.
   */
  renderMessageFooter?: (message: Message) => ReactNode;
};

/**
 * The prototype cards a message carries. The persist node stamps
 * `metadata.toolCallArtifacts`; only `ui_prototype` has a card in the rail.
 */
function prototypeArtifacts(message: Message): PrototypeArtifactData[] {
  const raw = (message.metadata as { toolCallArtifacts?: unknown } | null | undefined)
    ?.toolCallArtifacts;
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((artifact) =>
    artifact && typeof artifact === "object" && (artifact as { kind?: unknown }).kind === "ui_prototype"
      ? [(artifact as { data: PrototypeArtifactData }).data]
      : [],
  );
}

function cssEscape(value: string): string {
  if (typeof CSS !== "undefined" && typeof CSS.escape === "function") {
    return CSS.escape(value);
  }
  return value.replace(/["\\]/g, "\\$&");
}

/**
 * An approval whose payload carries an editable proposal (the field-map) renders
 * it editable in the DecisionCard, exactly like a Feed/gate proposal — so a chat
 * approval keeps the moat: the edited field-map reaches the resolve
 * (approval.respond `editedParameters`). Absent / wrong-shaped → null (a plain
 * capability approval, e.g. a toolkit expand).
 */
function approvalProposal(request: ApprovalRequest): EditableProposal | null {
  const p = (request.parameters as { proposal?: unknown }).proposal;
  if (
    p &&
    typeof p === "object" &&
    !Array.isArray(p) &&
    Array.isArray((p as { fields?: unknown }).fields)
  ) {
    return p as unknown as EditableProposal;
  }
  return null;
}

export function InlineChat(props: InlineChatProps) {
  const {
    conversationId,
    agentName,
    initialMessages,
    placeholder,
    onHumanMessageSent,
    focusMessageId,
    imperativeRef,
    composerSuggestions,
    composerVariant = "classic",
    openingMessage,
    focusOnAnswerRequest,
    scopeContext,
    renderMessageFooter,
    onSuggestionFill,
  } = props;

  const [messages, setMessages] = useState<Message[]>(initialMessages);
  // True once the mount-time history hydrate has settled for the current
  // binding. Gates the `openingMessage` opener so a returning user with prior
  // history never sees the opener flash before their thread loads. Reset to
  // false at the top of the hydrate effect on every binding change.
  const [hydrated, setHydrated] = useState(false);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState<string | null>(null);
  const [streamingToolCalls, setStreamingToolCalls] = useState<ToolCallDisplay[]>([]);
  const [typingParticipants, setTypingParticipants] = useState<
    InlineTypingParticipant[]
  >([]);
  // Pending tool/toolkit approvals surfaced in this conversation. Keyed by
  // approval id; populated by approval.requested and cleared by
  // approval.resolved.
  const [approvalsById, setApprovalsById] = useState<Record<string, ApprovalRequest>>({});
  const [approvalActionById, setApprovalActionById] = useState<
    Record<string, "approving" | "rejecting">
  >({});
  const streamingMessageIdRef = useRef<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Hosts can switch between persisted threads without remounting InlineChat.
  // Reset local transient state and load the existing messages whenever the
  // conversation changes so a resumed conversation shows its history
  // immediately.
  useEffect(() => {
    let cancelled = false;
    setMessages(initialMessages);
    setStreaming(null);
    setStreamingToolCalls([]);
    setTypingParticipants([]);
    setHydrated(false);
    streamingMessageIdRef.current = null;

    if (
      !conversationId ||
      initialMessages.length > 0 ||
      typeof fetch !== "function"
    ) {
      // Host already supplied messages (or there's nothing to hydrate).
      setHydrated(true);
      return () => {
        cancelled = true;
      };
    }

    void (async () => {
      try {
        const res = await fetch(
          `/api/conversations/${encodeURIComponent(conversationId)}/messages`,
          { credentials: "include" },
        );
        if (!res.ok) {
          if (!cancelled) setHydrated(true);
          return;
        }
        const persisted = (await res.json()) as Message[];
        if (cancelled) return;
        setMessages((current) => {
          const seen = new Set(persisted.map((message) => message.id));
          return [
            ...persisted,
            ...current.filter((message) => !seen.has(message.id)),
          ];
        });
        setHydrated(true);
      } catch {
        // Non-fatal: the WebSocket stream can still deliver new messages.
        if (!cancelled) setHydrated(true);
      }
    })();

    return () => {
      cancelled = true;
    };
    // `initialMessages` is intentionally captured when the conversation
    // changes. Including it would reset the chat whenever callers pass a
    // fresh [].
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  // Watchdog: auto-clear a typing participant if we haven't heard any
  // activity (typing.start, stream.delta, tool.called, tool.result) from
  // them in 15s. The agent runner emits typing.start at every node
  // transition so healthy turns refresh this every few seconds. A silent
  // gap longer than 15s means the runner errored before persist, the
  // socket dropped mid-turn, or typing.stop was lost — in each case the
  // indicator would otherwise stick on forever.
  const typingWatchdogsRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map(),
  );
  const refreshTypingWatchdog = useCallback((senderId: string) => {
    const existing = typingWatchdogsRef.current.get(senderId);
    if (existing) clearTimeout(existing);
    const timer = setTimeout(() => {
      typingWatchdogsRef.current.delete(senderId);
      setTypingParticipants((prev) => prev.filter((p) => p.senderId !== senderId));
    }, 15_000);
    typingWatchdogsRef.current.set(senderId, timer);
  }, []);
  const clearTypingWatchdog = useCallback((senderId: string) => {
    const existing = typingWatchdogsRef.current.get(senderId);
    if (existing) {
      clearTimeout(existing);
      typingWatchdogsRef.current.delete(senderId);
    }
  }, []);
  useEffect(() => {
    const watchdogs = typingWatchdogsRef.current;
    return () => {
      for (const timer of watchdogs.values()) clearTimeout(timer);
      watchdogs.clear();
    };
  }, []);

  const agentColor = getColor(agentName);

  // Collect all ticket identifiers from visible messages
  const allIdentifiers = useMemo(() => {
    const ids: string[] = [];
    for (const msg of messages) {
      ids.push(...extractTicketIdentifiers(msg.content));
    }
    return [...new Set(ids)];
  }, [messages]);

  const { tickets: resolvedTickets, loading: ticketsLoading } = useWorkItemResolver(allIdentifiers);

  const onMessage = useCallback(
    (msg: WsServerMessage) => {
      if (msg.type === "message") {
        // Filter by conversationId carried on the envelope (set by
        // broadcastToConversation in the backend WS handler).
        if (msg.conversationId !== conversationId) return;

        setMessages((prev) => {
          if (prev.some((m) => m.id === msg.data.id)) return prev;
          return [...prev, msg.data];
        });
        // The persisted message ends any in-progress typing affordance
        // for that sender (the agent finished, or the human message landed).
        clearTypingWatchdog(msg.data.senderId);
        setTypingParticipants((prev) =>
          prev.filter((p) => p.senderId !== msg.data.senderId),
        );
        // Clear streaming when final message arrives
        if (
          streamingMessageIdRef.current &&
          msg.data.id === streamingMessageIdRef.current
        ) {
          streamingMessageIdRef.current = null;
          setStreaming(null);
        }
      }

      if (
        msg.type === "stream.delta" &&
        "senderId" in msg &&
        "cumulative" in msg
      ) {
        if (msg.conversationId !== conversationId) return;
        setStreaming(msg.cumulative);
        streamingMessageIdRef.current = msg.messageId;
        // Streaming text is proof the agent is alive — refresh its watchdog
        // so a missed follow-up typing.start doesn't trip the auto-clear.
        refreshTypingWatchdog(msg.senderId);
      }

      if (msg.type === "stream.complete") {
        if (msg.conversationId !== conversationId) return;
        // Clear the streaming bubble here rather than waiting for the
        // `message` event — the final `msg.data.id` from message.created is
        // a fresh uuid persisted in persistNode, while the streaming bubble
        // is keyed by the turn's `streamMessageId`. Those never match, so
        // the id-based check in the `message` branch below never fires and
        // the streaming block lingers next to the persisted bubble (the
        // "spinner underneath the final reply" report). stream.complete
        // carries the same `messageId` as the deltas, which is the correct
        // key for tearing down the local streaming state.
        if (
          streamingMessageIdRef.current &&
          msg.messageId === streamingMessageIdRef.current
        ) {
          streamingMessageIdRef.current = null;
          setStreaming(null);
          setStreamingToolCalls([]);
        }
      }

      // tool.called / tool.result: only this conversation's tool activity.
      if (msg.type === "tool.called") {
        if (msg.conversationId !== conversationId) return;
        setStreamingToolCalls((prev) => {
          if (prev.some((call) => call.id === msg.toolCallId)) return prev;
          return [
            ...prev,
            {
              id: msg.toolCallId,
              tool: msg.tool,
              type: msg.toolType,
              params: msg.params,
              result: null,
              calledAt: msg.calledAt,
              completedAt: null,
              status: "running",
            },
          ];
        });
        refreshTypingWatchdog(msg.agentId);
      }

      if (msg.type === "typing.start") {
        // Only match typing scoped to this inline chat's conversation.
        if (msg.conversationId !== conversationId) return;
        setTypingParticipants((prev) => {
          if (prev.some((p) => p.senderId === msg.senderId)) return prev;
          return [
            ...prev,
            {
              senderId: msg.senderId,
              senderName: msg.senderName,
              senderType: msg.senderType,
            },
          ];
        });
        refreshTypingWatchdog(msg.senderId);
      }

      if (msg.type === "typing.stop") {
        if (msg.conversationId !== conversationId) return;
        clearTypingWatchdog(msg.senderId);
        setTypingParticipants((prev) =>
          prev.filter((p) => p.senderId !== msg.senderId),
        );
      }

      if (msg.type === "tool.result") {
        if (msg.conversationId !== conversationId) return;
        setStreamingToolCalls((prev) => {
          const idx = prev.findIndex((call) => call.id === msg.toolCallId);
          if (idx === -1) {
            return [
              ...prev,
              {
                id: msg.toolCallId,
                tool: msg.tool,
                type: "other",
                params: {},
                result: msg.result,
                calledAt: null,
                completedAt: msg.completedAt,
                status: isToolCallError(msg.result) ? "failed" : "completed",
              },
            ];
          }
          const next = [...prev];
          next[idx] = {
            ...next[idx]!,
            result: msg.result,
            completedAt: msg.completedAt,
            status: isToolCallError(msg.result) ? "failed" : "completed",
          };
          return next;
        });
        refreshTypingWatchdog(msg.agentId);
      }

      // Tool/toolkit approval requested mid-turn (e.g. @yagni expanding the
      // growth toolkit to read HubSpot). Only this conversation's approvals
      // render here.
      if (msg.type === "approval.requested") {
        if (msg.action.conversationId !== conversationId) return;
        setApprovalsById((prev) => ({ ...prev, [msg.action.id]: msg.action }));
      }

      if (msg.type === "approval.resolved") {
        if (msg.resolution.conversationId !== conversationId) return;
        const approvalId = msg.resolution.approvalId;
        setApprovalsById((prev) => {
          if (!(approvalId in prev)) return prev;
          const next = { ...prev };
          delete next[approvalId];
          return next;
        });
        setApprovalActionById((prev) => {
          if (!(approvalId in prev)) return prev;
          const next = { ...prev };
          delete next[approvalId];
          return next;
        });
      }

      if (msg.type === "error") {
        // A failed or denied approval respond comes back as a generic error
        // frame (no approvalId), so any in-flight optimistic "approving…" state
        // must clear wholesale — otherwise the card's buttons stay disabled
        // forever after a denial or transient failure (review 2026-07-03). The
        // card itself stays (no approval.resolved arrived), so a retry works.
        setApprovalActionById((prev) => (Object.keys(prev).length === 0 ? prev : {}));
      }
    },
    [
      conversationId,
      refreshTypingWatchdog,
      clearTypingWatchdog,
    ]
  );

  const { send } = useWebSocket({
    onMessage,
    onReconnect: (s) => {
      if (conversationId) {
        s({ type: "subscribe_conversation", conversationId });
      }
    },
  });

  // Subscribe on mount
  useEffect(() => {
    if (conversationId) {
      send({ type: "subscribe_conversation", conversationId });
      return () => {
        send({ type: "unsubscribe_conversation", conversationId });
      };
    }
    return undefined;
  }, [send, conversationId]);

  // Auto-scroll on new messages. When there's no activity yet (fresh
  // session), keep the scroll pinned to the top so the opener or empty
  // state is visible by default.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const hasActivity =
      messages.length > 0 ||
      streaming !== null ||
      streamingToolCalls.length > 0;
    el.scrollTop = hasActivity ? el.scrollHeight : 0;
  }, [messages, streaming, streamingToolCalls]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSend = useCallback(
    (overrideContent?: string) => {
      const trimmed = (overrideContent ?? input).trim();
      if (!trimmed) return;

      if (conversationId) {
        send({
          type: "sendConversationMessage",
          conversationId,
          content: trimmed,
          // Attach the surface's scope context additively (YAG-326): the
          // Library review rail rides the bound work item so @yagni answers
          // in-context. Omitted when the host supplies none.
          ...(scopeContext && Object.keys(scopeContext).length > 0
            ? { scopeContext }
            : {}),
        });
      }
      if (overrideContent === undefined) {
        setInput("");
      }
      onHumanMessageSent?.(trimmed);
    },
    [input, send, conversationId, onHumanMessageSent, scopeContext],
  );

  const decideApproval = useCallback(
    (
      request: ApprovalRequest,
      decision: "approved" | "rejected",
      editedParameters?: Record<string, unknown> | null,
    ) => {
      if (!conversationId) return;
      // Guard a double-send: once acting, the card stays until approval.resolved.
      if (approvalActionById[request.id]) return;
      setApprovalActionById((prev) => ({
        ...prev,
        [request.id]: decision === "approved" ? "approving" : "rejecting",
      }));
      send({
        type: "approval.respond",
        approvalId: request.id,
        conversationId,
        decision,
        // The edited field-map rides only when the payload was editable — an
        // unedited / plain approval sends no editedParameters (wiring unchanged).
        ...(decision === "approved" &&
        editedParameters &&
        Object.keys(editedParameters).length > 0
          ? { editedParameters }
          : {}),
      });
    },
    [send, conversationId, approvalActionById],
  );

  // Imperative bridge for parents that need to dispatch messages
  // programmatically (e.g. a seeded send into the @yagni rail). Re-assigns on
  // every render so the ref always captures the latest `handleSend` closure;
  // otherwise a long-lived ref would call a stale `send` after a conversation
  // switch.
  useEffect(() => {
    if (!imperativeRef) return;
    imperativeRef.current = {
      sendMessage: (text: string) => {
        handleSend(text);
      },
    };
    return () => {
      if (imperativeRef) imperativeRef.current = null;
    };
  }, [imperativeRef, handleSend]);

  // Answer on-ramp (the "Answer @yagni" affordance on a decision card): take the
  // cursor when asked. The mount-time check covers the case where this composer
  // mounts *because* the rail just opened in response to the same click, so the
  // event fired before this listener existed.
  useEffect(() => {
    if (!focusOnAnswerRequest) return;
    const focus = () => {
      inputRef.current?.focus();
      inputRef.current?.scrollIntoView({ block: "nearest" });
    };
    if (focusRequestedRecently()) focus();
    return onComposerFocusRequest(focus);
  }, [focusOnAnswerRequest]);

  // Skip system and autonomous context messages.
  // sender_type === 'support' is a first-class case (native feedback replies
  // from a YAGNI superadmin via God Mode) and always renders.
  const threadMessages = messages.filter((m) => {
    if (m.senderType === "system") return false;
    const meta = m.metadata as Record<string, unknown> | undefined;
    if (meta?.autonomousAction && meta?.autonomousSource === "work_item_discussion") return false;
    if (meta?.systemGenerated) return false;
    return true;
  });

  // Deep-link scroll-to-message primitive: when `focusMessageId` is set,
  // locate the matching bubble by `data-message-id`, scroll it into view,
  // and flash it via `data-focus-flash` for ~1.5s. Generic — nothing here
  // knows about work items, goals, or any specific parent type.
  useEffect(() => {
    if (!focusMessageId) return;
    const container = scrollRef.current;
    if (!container) return;
    const target = container.querySelector<HTMLElement>(
      `[data-message-id="${cssEscape(focusMessageId)}"]`,
    );
    if (!target) return;
    target.scrollIntoView({ block: "center", behavior: "smooth" });
    target.setAttribute("data-focus-flash", "true");
    const timeout = window.setTimeout(() => {
      target.removeAttribute("data-focus-flash");
    }, 1500);
    return () => {
      window.clearTimeout(timeout);
      target.removeAttribute("data-focus-flash");
    };
  }, [focusMessageId]);

  const pendingApprovals = Object.values(approvalsById);

  const hasAnyActivity =
    threadMessages.length > 0 ||
    streaming !== null ||
    streamingToolCalls.length > 0 ||
    typingParticipants.length > 0 ||
    pendingApprovals.length > 0;

  // Show @yagni's static opener only on a genuinely-empty thread, and only
  // after the history hydrate has settled (so a returning user with prior
  // turns never sees it flash before their chat loads). While it shows, the
  // composer takes a subtle invite cue so the eye lands on where to type.
  const showOpening =
    !hasAnyActivity && !!openingMessage && hydrated;

  return (
    <div className="flex h-full min-h-0 min-w-0 w-full flex-col">
      {/* Messages area */}
      <div
        ref={scrollRef}
        className="flex-1 min-h-0 min-w-0 overflow-y-auto px-5 py-4"
        style={{ maxHeight: "100%" }}
      >
        {!hasAnyActivity && (
          openingMessage ? (
            // Hold the opener until the hydrate settles: a returning user with
            // history must not see it flash before their thread loads.
            showOpening ? (
              <OpeningMessage text={openingMessage} agentName={agentName} />
            ) : null
          ) : (
            <ConversationEmptyState
              agentName={agentName}
              onSend={(text) => handleSend(text)}
            />
          )
        )}

        {threadMessages.map((msg) => {
          // Prototype cards ride the message's metadata (stamped by the persist
          // node), so they render identically for live-streamed completions and
          // history hydrates: both paths land in `messages`.
          const prototypes = prototypeArtifacts(msg);
          return (
            <Fragment key={msg.id}>
              <ChatBubble message={msg} agentColor={agentColor} resolvedTickets={resolvedTickets} ticketsLoading={ticketsLoading} />
              {prototypes.length > 0 ? (
                <div
                  className="pl-[26px] -mt-2 mb-3 flex flex-col gap-1.5 min-w-0"
                  data-testid="inline-chat-artifacts"
                >
                  {prototypes.map((prototype) => (
                    <PrototypeArtifact key={prototype.prototypeId} data={prototype} />
                  ))}
                </div>
              ) : null}
              {renderMessageFooter?.(msg)}
            </Fragment>
          );
        })}

        {/* Pending approvals — @yagni asked to use a gated capability (e.g. the
            growth toolkit for HubSpot). Rendered as the canonical DecisionCard
            (R2) so approving in chat and in the Feed are indistinguishable. The
            decision still ships over the socket (approval.respond) via
            decideApproval, carrying the edited field-map when the payload is
            editable — the moat's editedProposal reaches the resolve here too. */}
        {pendingApprovals.map((request) => {
          const toolkitDescription =
            typeof request.parameters.toolkitDescription === "string"
              ? request.parameters.toolkitDescription
              : null;
          const detail =
            toolkitDescription ??
            (request.details.map((d) => d.value).join(" · ") || undefined);
          const proposal = approvalProposal(request);
          return (
            <DecisionCard
              key={`approval-${request.id}`}
              className="mb-3 rounded-md border border-[var(--glass-border)] bg-[var(--glass-bg)] backdrop-blur-[var(--glass-blur)] backdrop-saturate-[1.15] shadow-[var(--shadow-staged)] px-3 py-2.5"
              style={{ "--dc": "var(--accent)" } as CSSProperties}
              eyebrow="Approval needed"
              title={request.title}
              proposal={proposal}
              description={detail}
              verbLabel={request.verb ?? "Approve"}
              effect={request.effect}
              onResolve={(intent, editedProposal) =>
                decideApproval(
                  request,
                  intent === "approve" ? "approved" : "rejected",
                  editedProposal,
                )
              }
              fieldIdPrefix={`approval-${request.id}`}
              testIds={{ root: "inline-approval-card" }}
            />
          );
        })}

        {/* Typing indicator — shown while an agent is thinking before the
            first delta lands. Hidden once streaming text appears, since the
            streaming bubble already carries its own activity affordance. */}
        {typingParticipants.length > 0 && !streaming && streamingToolCalls.length === 0 ? (
          <div className="mb-3">
            <TypingIndicator
              label={`${typingParticipants[0]!.senderName} is thinking`}
              avatarName={
                typingParticipants[0]!.senderType === "agent"
                  ? typingParticipants[0]!.senderName
                  : null
              }
              size="sm"
            />
          </div>
        ) : null}

        {/* Streaming indicator — shown while text is streaming OR tools are running */}
        {(streaming || streamingToolCalls.length > 0) && (
          <div className="mb-3">
            <div className="flex items-center gap-1.5 mb-1">
              <span
                className="w-6 h-6 rounded flex items-center justify-center text-[11px] font-bold"
                style={{
                  background: `color-mix(in srgb, ${agentColor} 12%, transparent)`,
                  color: agentColor,
                  fontFamily: "var(--font-display)",
                }}
              >
                {agentName.replace(/^@/, "").charAt(0).toUpperCase()}
              </span>
              <span className="text-[12.5px] font-medium" style={{ color: agentColor }}>
                {agentName}
              </span>
              <Loader2 size={10} className="animate-spin" style={{ color: "var(--text-tertiary)" }} />
            </div>
            {streaming && (
              <div
                className="text-[15px] leading-relaxed pl-[30px] markdown-rendered break-words"
                style={{ color: "var(--text-primary)" }}
                {...markdownHtml(streaming)}
              />
            )}
            {streamingToolCalls.length > 0 && (
              <ToolCallGroup calls={streamingToolCalls} className="pl-[30px] mt-1.5">
                <div className="flex flex-col gap-1">
                {streamingToolCalls.map((call) => {
                  const Icon =
                    call.status === "running"
                      ? Loader2
                      : call.status === "failed"
                        ? AlertCircle
                        : CheckCircle2;
                  const iconColor =
                    call.status === "running"
                      ? "var(--text-tertiary)"
                      : call.status === "failed"
                        ? "var(--error)"
                        : "var(--success)";
                  return (
                    <div
                      key={call.id}
                      className="flex items-center gap-1.5 rounded-md px-2 py-1 text-[11px]"
                      style={{
                        background: "var(--print-bg)",
                        border: "1px solid var(--print-border)",
                        color: "var(--text-secondary)",
                      }}
                    >
                      <Icon
                        size={11}
                        className={call.status === "running" ? "animate-spin" : ""}
                        style={{ color: iconColor }}
                      />
                      <span style={{ color: "var(--text-primary)" }}>
                        <ToolCallLabel toolName={call.tool} />
                      </span>
                      <span className="ml-auto text-[10px]" style={{ color: iconColor }}>
                        {call.status}
                      </span>
                    </div>
                  );
                })}
                </div>
              </ToolCallGroup>
            )}
          </div>
        )}
      </div>

      {/* Input area: the classic composer's styles live in index.css under
          `.conversation-composer` (border, focus ring, send-button morph). The
          "tri" variant swaps in the Triptych `.tri-chat-foot` (chip row +
          `.cw-composer`) used by the in-stage @yagni rails. */}
      {composerVariant === "dock" ? (
        composerSuggestions && composerSuggestions.length > 0 ? (
          <div className="prompts">
            {composerSuggestions.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => {
                  if (s.endsWith("…")) {
                    onSuggestionFill?.(s.slice(0, -1));
                    return;
                  }
                  handleSend(s);
                }}
              >
                {s}
              </button>
            ))}
          </div>
        ) : null
      ) : composerVariant === "tri" ? (
        <div className="tri-chat-foot">
          {composerSuggestions && composerSuggestions.length > 0 && (
            <div className="cw-suggest">
              {composerSuggestions.map((s) => (
                <button
                  key={s}
                  type="button"
                  className="cw-sugg"
                  onClick={() => {
                    // A chip that trails off ("Start a ticket for …") is the
                    // start of a sentence: it fills the composer for the
                    // person to finish instead of sending.
                    if (s.endsWith("…")) {
                      setInput(s.slice(0, -1));
                      inputRef.current?.focus();
                      return;
                    }
                    handleSend(s);
                  }}
                >
                  {s}
                </button>
              ))}
            </div>
          )}
          <div className="cw-composer" data-invite={showOpening ? "true" : undefined}>
            <Textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder={placeholder ?? `Reply to ${agentName}…`}
              data-testid="inline-chat-input"
              rows={1}
              autosize
              className="min-w-0 flex-1 border-0 bg-transparent rounded-none focus-visible:ring-0 focus-visible:outline-none p-0 resize-none"
            />
            <button
              type="button"
              onClick={() => handleSend()}
              disabled={!input.trim()}
              aria-label="Send message"
              className="cw-send"
              data-state={input.trim() ? "ready" : "idle"}
            >
              <Send size={15} />
            </button>
          </div>
          {/* No trust caption here. The composer used to carry a blanket
              "it never acts on its own" guarantee, which the product does not
              keep: approved gates ship completion actions to source systems,
              and a graduated Team ships reversible ones without a per-item
              approval. A safety claim a user can catch being false costs more
              trust than the line ever bought. What @yagni will and will not do
              is stated where it is true and checkable: on the gate that stages
              the act and on the receipt that records it. */}
        </div>
      ) : (
        <div className="px-4 pb-4 pt-2 shrink-0">
          <ComposerSuggestions
            suggestions={composerSuggestions}
            hasHumanReply={threadMessages.some((m) => m.senderType === "human")}
            onPick={(text) => handleSend(text)}
          />
          <div className="conversation-composer">
            <Textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder={placeholder ?? `Message ${agentName}...`}
              data-testid="inline-chat-input"
              rows={1}
              autosize
              className="min-w-0 flex-1 border-0 bg-transparent rounded-none focus-visible:ring-0 focus-visible:outline-none p-0"
            />
            <button
              type="button"
              onClick={() => handleSend()}
              disabled={!input.trim()}
              aria-label="Send message"
              className="conversation-send"
              data-state={input.trim() ? "ready" : "idle"}
            >
              <Send size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   Empty state
   ═══════════════════════════════════════════════════════════ */

/**
 * Shared empty-state for every conversation surface. Renders a branded
 * amber avatar, a two-line prompt, and an optional suggestion row. The
 * suggestion chips populate the composer instead of sending directly —
 * that way the user can tweak the wording before committing, which matches
 * how product teams actually draft with an agent.
 */
function ConversationEmptyState({
  agentName,
  onSend,
}: {
  agentName: string;
  onSend: (text: string) => void;
}) {
  const displayName = agentName.replace(/^@/, "");
  const suggestions = [
    "Catch me up on what's happening here",
    "What should I focus on first?",
    "Draft something for me",
  ];
  return (
    <div
      className="conversation-empty flex flex-col items-center justify-center px-6 py-5 text-center gap-3 h-full min-h-[140px]"
    >
      <span className="conversation-empty-avatar conversation-empty-avatar-sm">
        <Sparkles size={14} strokeWidth={2.25} />
      </span>
      <p
        className="text-[12px]"
        style={{
          color: "var(--text-secondary)",
          fontFamily: "var(--font-sans)",
          letterSpacing: "0.01em",
        }}
      >
        Tap to ask{" "}
        <span style={{ color: "var(--accent)", fontWeight: 500 }}>
          @{displayName}
        </span>
        , or type below:
      </p>
      <div className="flex flex-wrap items-center justify-center gap-1.5">
        {suggestions.map((s) => (
          <button
            key={s}
            type="button"
            className="conversation-empty-chip"
            onClick={() => onSend(s)}
          >
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   Chat Bubble
   ═══════════════════════════════════════════════════════════ */

/**
 * OpeningMessage — @yagni's static, presentational opener for an empty thread.
 *
 * Rendered like an agent ChatBubble (the Y avatar + "@yagni" + the line) but
 * with no timestamp, no citations, and no ticket resolution: it is a greeting,
 * not a persisted turn. It is never written to the conversation and never sent
 * (no LLM turn). The instant any real message exists, the host's `hasAnyActivity`
 * gate hides it and the thread takes over.
 *
 * Exported so a *ChatRail can render the IDENTICAL bubble as its pre-resolve
 * placeholder (while useThreadSession is still resolving the conversationId), so
 * the opener doesn't reposition or change copy when the thread mounts.
 */
export function OpeningMessage({
  text,
  agentName,
}: {
  text: string;
  agentName: string;
}) {
  const agentColor = getColor(agentName);
  return (
    <div className="mb-3" data-testid="inline-chat-opening">
      <div className="flex items-center gap-1.5 mb-1">
        <span
          className="w-6 h-6 rounded flex items-center justify-center text-[11px] font-bold"
          style={{
            background: `color-mix(in srgb, ${agentColor} 14%, transparent)`,
            color: agentColor,
            fontFamily: "var(--font-display)",
          }}
        >
          {agentName.replace(/^@/, "").charAt(0).toUpperCase()}
        </span>
        <span className="text-[12.5px] font-medium" style={{ color: agentColor }}>
          {agentName}
        </span>
      </div>
      <div
        className="text-[15px] leading-relaxed pl-[30px] break-words whitespace-pre-wrap"
        style={{ color: "var(--text-primary)" }}
      >
        {text}
      </div>
    </div>
  );
}

export function ChatBubble({ message, agentColor, resolvedTickets, ticketsLoading }: { message: Message; agentColor: string; resolvedTickets?: Map<string, import("@yagni/shared").Ticket>; ticketsLoading?: boolean }) {
  const isHuman = message.senderType === "human";
  const isAgent = message.senderType === "agent";
  const isSupport = message.senderType === "support";

  // Support replies (Jack from YAGNI via God Mode) render in a warm amber
  // distinct from both the agent accent and the human grey, so the
  // submitter instantly sees "a real human from YAGNI wrote this".
  const supportColor = "var(--warning)";
  const color = isSupport
    ? supportColor
    : isAgent
      ? agentColor
      : "var(--text-primary)";
  const nameColor = isHuman ? "var(--text-secondary)" : color;
  const avatarBg = isHuman
    ? "var(--bg-base)"
    : `color-mix(in srgb, ${color} 14%, transparent)`;
  // Render markdown for every visible sender. Human messages can include
  // formatted content (e.g. the heartbeat "Ask @yagni" seed sends a
  // markdown breakdown of the run); agent + support replies always need
  // it. Everything goes through DOMPurify in renderMarkdown.
  const renderRich = isHuman || isAgent || isSupport;

  // Inline citations (Task 7 / agent-memory L5). Synthesis tools that
  // follow the SYNTHESIS_PROVENANCE_RULES contract attach a `citations`
  // array to message metadata. When present, render a non-markdown
  // splice path so we can place CitationChip nodes inline at the
  // (start, end) offsets the model emitted. Messages without citations
  // keep the existing markdown render path (compat with older bundles
  // and non-synthesis chat turns).
  const citations: Citation[] = Array.isArray(
    (message.metadata as { citations?: unknown } | null | undefined)?.citations,
  )
    ? ((message.metadata as { citations?: Citation[] }).citations ?? [])
    : [];
  const hasCitations = isAgent && citations.length > 0;
  // Resolve `{ url, summary }` per citation via /api/citations/resolve.
  // The resolvers start as no-ops and update as fetches complete; the
  // chip falls back to its popover until the URL/summary is resolved,
  // so the user never sees a broken click target.
  const { resolveUrl, resolveSummary } = useCitationResolvers(
    hasCitations ? citations : EMPTY_CITATIONS,
  );

  return (
    <div className="mb-3" data-message-id={message.id}>
      <div className="flex items-center gap-1.5 mb-1">
        <span
          className="w-6 h-6 rounded flex items-center justify-center text-[11px] font-bold"
          style={{
            background: avatarBg,
            color: isHuman ? "var(--text-secondary)" : color,
            fontFamily: "var(--font-display)",
          }}
        >
          {message.senderName.replace(/^@/, "").charAt(0).toUpperCase()}
        </span>
        <span
          className="text-[12.5px] font-medium"
          style={{ color: nameColor }}
        >
          {message.senderName}
        </span>
        {isSupport && (
          <span
            className="text-[9px] font-semibold uppercase tracking-wider px-1 py-[1px] rounded"
            style={{
              background: `color-mix(in srgb, ${supportColor} 18%, transparent)`,
              color: supportColor,
            }}
          >
            YAGNI team
          </span>
        )}
        <span className="text-[10px]" style={{ color: "var(--text-tertiary)" }}>
          {formatTime(message.createdAt)}
        </span>
      </div>
      {hasCitations ? (
        <div
          data-testid="chat-bubble-with-citations"
          className="text-[15px] leading-relaxed pl-[30px] break-words whitespace-pre-wrap"
          style={{ color: "var(--text-primary)" }}
        >
          {spliceCitationsIntoText(message.content, citations, {
            resolveUrl,
            resolveSummary,
          })}
        </div>
      ) : renderRich ? (
        <div
          className="text-[15px] leading-relaxed pl-[30px] markdown-rendered break-words"
          style={{ color: "var(--text-primary)" }}
          {...markdownHtml(message.content)}
        />
      ) : (
        <div
          className="text-[15px] leading-relaxed pl-[30px] whitespace-pre-wrap"
          style={{ color: "var(--text-primary)" }}
        >
          {message.content}
        </div>
      )}
      {resolvedTickets && (() => {
        const ids = extractTicketIdentifiers(message.content);
        if (ids.length === 0) return null;
        return (
          <div className="flex flex-wrap gap-1 mt-1 pl-[30px]">
            {ids.map((id) => (
              <WorkItemMention
                key={id}
                identifierText={id}
                ticket={resolvedTickets.get(id) ?? null}
                loading={ticketsLoading && !resolvedTickets.has(id)}
              />
            ))}
          </div>
        );
      })()}
    </div>
  );
}

function formatTime(iso: string): string {
  const date = new Date(iso);
  const time = date.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  const now = new Date();
  const isToday =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();
  if (isToday) return time;
  // Older than today: a bare clock time on a month-old message reads as
  // today's — date it ("Jun 11 · 3:36 PM").
  const day = date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  return `${day} · ${time}`;
}
